import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, TabControlAPI, TabWithContentAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AuthenticationService, LanguageService, LocaleService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Validation } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { applicationContext, foundationContext, requestInformation } from '@philips-internal/oswald-ui-foundation';

import { FragmentNames, UserItemAliases } from '../../codegen';
import {
  Establishment,
  FormUserLocation,
  FormUserPassword,
  UserLocationRequest,
  UserPasswordRequest,
  UserSettingsRequest,
} from '../../interfaces';
import { UserService } from '../../services';
import BaseController from '../BaseController';

import './user-item-controller.css';

@Controller({ fragment: FragmentNames.USER_ITEM })
export default class UserItemController extends BaseController {

  @Inject(UserItemAliases.FORM_USER)
  formUser!: FormAPI;

  @Inject(UserItemAliases.FORM_CHANGE_PASSWORD)
  formChangePassword!: FormAPI;

  @Inject(UserItemAliases.AUTOCOMPLETE_LANGUAGE)
  autocompleteLanguage!: AutocompleteAPI;

  @Inject(UserItemAliases.AUTOCOMPLETE_LOCALE)
  autocompleteLocale!: AutocompleteAPI;

  /*@Inject(UserItemAliases.PHOTO_UPLOADER_AVATAR)
  photoUploaderAvatar!: PhotoUploaderAPI;*/

  @Inject(UserItemAliases.AUTOCOMPLETE_ESTABLISHMENT)
  autocompleteEstablishment!: AutocompleteAPI;

  @Inject(UserItemAliases.FIELD_CURRENT_PASSWORD)
  fieldCurrentPassword!: FieldAPI;

  @Inject(UserItemAliases.FIELD_NEW_PASSWORD)
  fieldNewPassword!: FieldAPI;

  @Inject(UserItemAliases.FIELD_NEW_PASSWORD_CONFIRMATION)
  fieldNewPasswordConfirmation!: FieldAPI;

  @Inject(UserItemAliases.TAB_CONTROL_0)
  tabControl0!: TabControlAPI;

  @Inject(UserItemAliases.TAB_WITH_CONTENT_0)
  tabWithContent0!: TabWithContentAPI;

  @Inject(UserItemAliases.TAB_WITH_CONTENT_1)
  tabWithContent1!: TabWithContentAPI;

  @Inject(UserItemAliases.TAB_WITH_CONTENT_CHANGE_PASSWORD)
  tabChangePassword!: TabWithContentAPI;

  @Inject(UserItemAliases.FETCH_ESTABLISHMENT)
  fetchEstablishment!: FetchAPI;

  id!: number;
  languages = this.getLanguages();
  locales = this.getLocales();
  establishments = this.getEstablishments();
  isUserPhilips = false;
  isUpdateListCustomers = false;

  getLanguages(): Record<string, Value>[] {
    const languages = LanguageService.getSupportedLanguages();
    return languages.map(language => ({
      label: MessageService.translate(`language.${language}`),
      value: language,
    }));
  }

  getLocales(): Record<string, Value>[] {
    const locales = LocaleService.getSupportedLocales();
    const data: Record<string, Value>[] = [];
    if (locales.length === 0) {
      const locale = LocaleService.getCurrentLocale();
      data.push({
        label: MessageService.translate(`language.${locale}`),
        value: locale,
      });
      return data;
    }
    locales.forEach(locale => data.push({
      label: MessageService.translate(`language.${locale}`),
      value: locale,
    }));
    return data;
  }

  getEstablishments(): Record<string, Value>[] {
    const establishments = foundationContext.getAvailableEstablishments();
    if (establishments && establishments.length > 0) {
      return establishments.map(establishment => ({
        dsCliente: establishment.name,
        value: establishment.id,
      }));
    }
    return [];
  }

  @OnMount(UserItemAliases.TAB_WITH_CONTENT_CHANGE_PASSWORD)
  setActiveTab(): void {
    const urlParamns = new URLSearchParams(window.location.search);
    const parametro = urlParamns.get('tempPass');
    if(parametro){
      this.tabWithContent0.setEnabled(false);
      this.tabWithContent0.setVisible(false);

      this.tabWithContent1.setEnabled(false);
      this.tabWithContent1.setVisible(false);

      this.tabControl0.setActiveTab(this.tabChangePassword.getName());
    }
  }

  @OnMount({ target: UserItemAliases.LAYOUT_USER_ITEM })
  onMount(): void {
    ToastService.closeAll();
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: 'user' } });
    dispatchEvent(event);
    this.id = Number(this.getPathId());
    const urlParamns = new URLSearchParams(window.location.search);
    const parametro = urlParamns.get('tempPass');
    if(parametro){
      ToastService.open({
        dismissible: false,
        id: 'update-success',
        message: 'Troque a senha temporaria para continuar acessando o sistema.',
        placement: Placement.TOP,
        showIcon: true,
        level: ToastLevel.INFORMATION,
        title: this.getLoggedUser().name,
        type: ToastType.BUBBLE,
      });
    }
  }

  @OnCancel({ target: [UserItemAliases.FORM_USER, UserItemAliases.FORM_SETTINGS, UserItemAliases.FORM_CHANGE_PASSWORD] })
  onCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount({ target: UserItemAliases.FORM_USER })
  onMountForm(): void {
    this.formUser.setLoading(true);
    const user = this.getLoggedUser();
    this.formUser.setRecord({
      language: this.languages.find(l => l.value === user.language),
      locale: this.locales.find(l => l.value === user.locale),
    });
    this.formUser.setData({
      language: this.languages.find(l => l.value === user.language),
      locale: this.locales.find(l => l.value === user.locale),
    });
    this.formUser.setLoading(false);
  }

  @OnMount({ target: UserItemAliases.FORM_CHANGE_PASSWORD })
  onMountFormChangePassword(): void {
    this.formChangePassword.setLoading(true);
    this.formChangePassword.setData({
      currentPassword: null,
      newPassword: null,
      newPasswordConfirmation: null,
    });
    this.addPasswordValidators();
    this.formChangePassword.setLoading(false);
  }

  @OnMount({ target: UserItemAliases.AUTOCOMPLETE_LANGUAGE })
  onMountAutocompleteLanguage(): void {
    this.autocompleteLanguage.setData(this.languages);
  }

  @OnMount({ target: UserItemAliases.AUTOCOMPLETE_LOCALE })
  onMountAutocompleteLocale(): void {
    this.autocompleteLocale.setData(this.locales);
  }

  @OnMount({ target: UserItemAliases.AUTOCOMPLETE_ESTABLISHMENT })
  onMountAutocompleteEstablishment(): void {
    this.autocompleteEstablishment.setData(this.establishments);
    const establishment = foundationContext.getEstablishment();
    if (establishment) {
      const current = this.establishments.find(e => e.value === establishment.id);
      this.autocompleteEstablishment.setValue(current);
    }
  }

  @OnAfterFetch( UserItemAliases.FETCH_ESTABLISHMENT )
  onAfterFetchEstablishment(): void {
    if (!this.isUserPhilips) {
      this.getIsUserPhilips();
    }
    if (!this.isUpdateListCustomers) {
      this.onMountAutocompleteEstablishment();
      this.isUpdateListCustomers = this.isUserPhilips;
    }
  }

  /*@OnMount({ target: UserItemAliases.PHOTO_UPLOADER_AVATAR })
  onMountPhotoUploaderAvatar(): void {
    const user = this.getLoggedUser();
    this.photoUploaderAvatar.setValue(FileService.base64ToBlob(user.image));
  }

  @OnChange({ target: UserItemAliases.PHOTO_UPLOADER_AVATAR })
  onChangePhotoUploaderAvatar(event: ChangeEvent<Blob>): void {
    if (this.photoUploaderAvatar.getValue() !== event.value) {
      this.photoUploaderAvatar.setValue(event.value);
    }
  }*/

  @OnSubmit({ target: UserItemAliases.FORM_USER })
  onSubmitFormUser(): void {
    const record = this.formUser.getRecord() as FormUserLocation;
    const user: UserLocationRequest = {
      id: this.id,
      userName: this.getLoggedUser().username,
      language: record.language.value,
      locale: record.locale.value,
    };
    void UserService.updateLocation(user).then(() => {
      LanguageService.setCurrentLanguage(user.language);
      LocaleService.setCurrentLocale(user.locale);
      applicationContext.popNavigationItem();
      AuthenticationService.redirectToHome();
      this.showSuccessMessage();
    }).catch(error => {
      this.showErrorMessage(error.status);
    });
  }

  @OnSubmit({ target: UserItemAliases.FORM_SETTINGS })
  onSubmitFormSettings(): void {
    const establishment = this.autocompleteEstablishment.getValue() as Establishment;
    /*void FileService.blobToBase64(this.photoUploaderAvatar.getValue()).then(result => {
      const photo = (result ? (result as string) : undefined);
    });*/
    const user: UserSettingsRequest = {
      id: this.id,
      userName: this.getLoggedUser().username,
      photo: undefined,
      establishmentId: this.isUserPhilips && establishment.value === undefined ? parseInt(establishment.nrSequencia as string) : parseInt(establishment.value),
    };
    void UserService.updateSettings(user).then(() => {
      void requestInformation(new AbortController(), true)
        .then(() => {
          applicationContext.popNavigationItem();
          AuthenticationService.redirectToHome();
          this.showSuccessMessage();
        });
    }).catch(error => {
      this.showErrorMessage(error.status);
    });

  }

  @OnSubmit({ target: UserItemAliases.FORM_CHANGE_PASSWORD })
  onSubmitFormChangePassword(): void {
    const record = this.formChangePassword.getRecord() as FormUserPassword;
    const user: UserPasswordRequest = {
      id: this.id,
      userName: this.getLoggedUser().username,
      ...record,
    };
    UserService.updateChangePassword(user).then(() => {
      this.showSuccessMessage();
      void AuthenticationService.logout().then(() => {
        applicationContext.popNavigationItem();
        AuthenticationService.redirectToLogin();
      });
    }).catch(error => {
      this.showErrorMessage(error.status);
    });
  }

  private showSuccessMessage(): void {
    ToastService.open({
      dismissible: false,
      id: 'update-success',
      message: 'update.success',
      placement: Placement.TOP,
      showIcon: true,
      level: ToastLevel.SUCCESS,
      title: this.getLoggedUser().name,
      type: ToastType.BUBBLE,
    });
  }

  showErrorMessage = (status: number): void => {
    let message: string;
    if (status === 404) {
      message = 'user.error-not-found';
    } else {
      message = 'user.error-unexpected-create';
    }
    ToastService.open({
      dismissible: false,
      id: 'dialog-error',
      message,
      placement: Placement.TOP,
      showIcon: true,
      level: ToastLevel.ERROR,
      title: this.getLoggedUser().name,
      type: ToastType.BUBBLE,
    });
  };

  private addPasswordValidators(): void {
    this.fieldNewPassword.addLocalValidator('noEqualsPassword', (value: Value): Promise<Validation[]> => {
      let valid = true;
      try {
        const data = this.formChangePassword.getRecord() as FormUserPassword;
        valid = value !== (data.currentPassword ?? null);
      } catch (e) {
        console.log(e);
      }
      return Promise.resolve([{
        message: !valid ? 'user.password.equals' : '',
        name: 'noEqualsPassword',
        valid,
      }]);
    });
    this.fieldNewPasswordConfirmation.addLocalValidator('equalsPassword', (value: Value): Promise<Validation[]> => {
      let valid = true;
      try {
        const data = this.formChangePassword.getRecord() as FormUserPassword;
        valid = value === (data.newPassword ?? null);
      } catch (e) {
        console.log(e);
      }
      return Promise.resolve([{
        message: !valid ? 'user.password.no-equals' : '',
        name: 'equalsPassword',
        valid,
      }]);
    });
  }

  getIsUserPhilips(): void {
    const id = this.getLoggedUser().id;
    void UserService.isUserPhilips(id).then(response =>{
      this.isUserPhilips =  response;
      this.autocompleteEstablishment.setLocalData(!response);
    }).catch(() => {
      this.isUserPhilips = false;
    });
  }

}
